
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { Card } from '@/components/ui/card';

interface BandBoundaries {
  line1: number;
  line2: number;
  line3: number;
  line4: number;
}

interface BellCurveChartProps {
  data: number[];
  boundaries?: BandBoundaries;
  onBoundariesChange?: (boundaries: BandBoundaries) => void;
  onReset?: () => void;
  refreshTrigger?: number;
}

const BellCurveChart: React.FC<BellCurveChartProps> = ({ 
  data, 
  boundaries: externalBoundaries,
  onBoundariesChange,
  onReset,
  refreshTrigger = 0
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const animationFrameRef = useRef<number | null>(null);
  
  const [boundaries, setBoundaries] = useState<BandBoundaries>(
    externalBoundaries || {
      line1: 1.72,
      line2: 3.39,
      line3: 4.28,
      line4: 5.0
    }
  );
  const [dragging, setDragging] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);

  const width = 800;
  const height = 350;
  const padding = 40;

  // Update boundaries when external boundaries change
  useEffect(() => {
    if (externalBoundaries) {
      console.log("BellCurveChart: External boundaries received", externalBoundaries);
      // Ensure line4 always exists
      const safeBoundaries = {
        ...externalBoundaries,
        line4: externalBoundaries.line4 || 5.0
      };
      setBoundaries(safeBoundaries);
    }
  }, [externalBoundaries]);

  // Force redraw when refresh trigger changes
  useEffect(() => {
    console.log("BellCurveChart: Refresh trigger changed to", refreshTrigger);
    drawChart();
  }, [refreshTrigger]);

  // Normal distribution curve calculation for range 0-5 with proper tapering at both ends
  const chartData = useMemo(() => {
    const normalDistribution = (x: number, mean: number = 2.5, stdDev: number = 0.8) => {
      const exponent = -0.5 * Math.pow((x - mean) / stdDev, 2);
      return (1 / (stdDev * Math.sqrt(2 * Math.PI))) * Math.exp(exponent);
    };

    const curvePoints: Array<{x: number, y: number}> = [];
    // Generate points from 0 to 5 with finer resolution for smoother curve
    for (let x = 0; x <= 5; x += 0.005) {
      const y = normalDistribution(x);
      curvePoints.push({ x, y });
    }

    return { curvePoints, normalDistribution };
  }, []);

  // Data point distribution on the curve
  const distributedDataPoints = useMemo(() => {
    if (!data || data.length === 0) return [];

    const points: Array<{x: number, y: number}> = [];
    
    data.forEach(rating => {
      if (rating >= 0 && rating <= 5) {
        const x = rating;
        const curveHeight = chartData.normalDistribution(x);
        const y = curveHeight * 0.6;
        
        points.push({ x, y });
      }
    });

    return points;
  }, [data, chartData.normalDistribution]);

  // Chart drawing function ensuring proper tapering at both ends
  const drawChart = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Clear canvas completely
    ctx.clearRect(0, 0, width, height);
    
    // Set up coordinate system for range 0-5
    const xMin = 0;
    const xMax = 5;
    const yMin = 0;
    const yMax = 0.5;

    const scaleX = (width - 2 * padding) / (xMax - xMin);
    const scaleY = (height - 2 * padding) / (yMax - yMin);

    const toCanvasX = (x: number) => padding + (x - xMin) * scaleX;
    const toCanvasY = (y: number) => height - padding - (y - yMin) * scaleY;

    // Draw axes
    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(padding, height - padding);
    ctx.lineTo(width - padding, height - padding);
    ctx.moveTo(padding, padding);
    ctx.lineTo(padding, height - padding);
    ctx.stroke();

    // Draw axis labels from 0 to 5
    ctx.fillStyle = '#ffffff';
    ctx.font = '12px Arial';
    ctx.textAlign = 'center';
    
    for (let i = 0; i <= 5; i++) {
      const x = toCanvasX(i);
      ctx.fillText(i.toString(), x, height - padding + 15);
      
      // Grid lines
      ctx.strokeStyle = '#444444';
      ctx.lineWidth = 1;
      ctx.beginPath();
      ctx.moveTo(x, padding);
      ctx.lineTo(x, height - padding);
      ctx.stroke();
    }

    // Draw bell curve with smooth tapering to both x-axis endpoints
    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 3;
    ctx.beginPath();
    
    // Start exactly at x-axis at x=0
    ctx.moveTo(toCanvasX(0), toCanvasY(0));
    
    // Draw the curve points smoothly
    chartData.curvePoints.forEach((point, index) => {
      const canvasX = toCanvasX(point.x);
      const canvasY = toCanvasY(point.y);
      
      if (index === 0) {
        ctx.moveTo(canvasX, canvasY);
      } else {
        ctx.lineTo(canvasX, canvasY);
      }
    });
    
    // Ensure the curve ends exactly at x-axis at x=5
    ctx.lineTo(toCanvasX(5), toCanvasY(0));
    ctx.stroke();

    const boundaryValues = [boundaries.line1, boundaries.line2, boundaries.line3, boundaries.line4 || 5.0];
    const colors = ['#ff6b6b', '#ffd93d', '#6bcf7f', '#4dabf7']; // D, C, B, A

    // Fill areas between boundaries with updated ranges
    const ranges = [
      { start: 0, end: boundaries.line1, label: 'D', color: colors[0] },
      { start: boundaries.line1, end: boundaries.line2, label: 'C', color: colors[1] },
      { start: boundaries.line2, end: boundaries.line3, label: 'B', color: colors[2] },
      { start: boundaries.line3, end: 5, label: 'A', color: colors[3] }
    ];

    ranges.forEach((range, i) => {
      ctx.fillStyle = range.color + '40';
      ctx.beginPath();
      ctx.moveTo(toCanvasX(range.start), height - padding);
      
      chartData.curvePoints.forEach(point => {
        if (point.x >= range.start && point.x <= range.end) {
          ctx.lineTo(toCanvasX(point.x), toCanvasY(point.y));
        }
      });
      
      ctx.lineTo(toCanvasX(range.end), height - padding);
      ctx.closePath();
      ctx.fill();

      // Add band labels
      const midX = (range.start + range.end) / 2;
      ctx.fillStyle = '#ffffff';
      ctx.font = 'bold 14px Arial';
      ctx.textAlign = 'center';
      ctx.fillText(range.label, toCanvasX(midX), height - padding + 30);
    });

    // Draw boundary lines (excluding the first and last boundaries)
    [boundaries.line1, boundaries.line2, boundaries.line3].forEach((boundary, index) => {
      ctx.strokeStyle = '#ffffff';
      ctx.lineWidth = 2;
      ctx.setLineDash([5, 5]);
      ctx.beginPath();
      ctx.moveTo(toCanvasX(boundary), padding);
      ctx.lineTo(toCanvasX(boundary), height - padding);
      ctx.stroke();
      ctx.setLineDash([]);

      // Draw draggable handles
      ctx.fillStyle = '#ffffff';
      ctx.beginPath();
      ctx.arc(toCanvasX(boundary), height - padding + 8, 6, 0, 2 * Math.PI);
      ctx.fill();
      ctx.strokeStyle = '#000000';
      ctx.lineWidth = 2;
      ctx.stroke();
    });

    // Plot distributed data points
    if (distributedDataPoints.length > 0) {
      ctx.fillStyle = '#ff0000';
      distributedDataPoints.forEach(point => {
        const x = toCanvasX(point.x);
        const y = toCanvasY(point.y);
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, 2 * Math.PI);
        ctx.fill();
      });
    }

    console.log("BellCurveChart: Chart redrawn with boundaries", boundaries);
  }, [boundaries, chartData, distributedDataPoints]);

  // Effect to redraw when boundaries or data change
  useEffect(() => {
    drawChart();
  }, [drawChart]);

  const getMousePos = (canvas: HTMLCanvasElement, event: MouseEvent | React.MouseEvent) => {
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  };

  const handleMouseDown = (event: React.MouseEvent) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const mousePos = getMousePos(canvas, event);
    const x = mousePos.x;
    const y = mousePos.y;

    const boundaryValues = [boundaries.line1, boundaries.line2, boundaries.line3];
    const scaleX = (width - 2 * padding) / 5; // Updated for 0-5 range
    
    boundaryValues.forEach((boundary, index) => {
      const handleX = padding + boundary * scaleX;
      const handleY = height - padding + 8;
      
      const distance = Math.sqrt((x - handleX) ** 2 + (y - handleY) ** 2);
      if (distance <= 10) {
        setDragging(`line${index + 1}`);
        setIsDragging(true);
        event.preventDefault();
      }
    });
  };

  const handleMouseMove = useCallback((event: React.MouseEvent) => {
    if (!dragging || !isDragging) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const mousePos = getMousePos(canvas, event);
    const x = mousePos.x;
    const scaleX = (width - 2 * padding) / 5; // Updated for 0-5 range
    let newValue = Math.max(0.1, Math.min(4.9, (x - padding) / scaleX));

    // Apply constraints to maintain reasonable positioning
    if (dragging === 'line1') {
      newValue = Math.max(1.5, Math.min(2.0, newValue));
    } else if (dragging === 'line2') {
      newValue = Math.max(3.0, Math.min(3.8, newValue));
    } else if (dragging === 'line3') {
      newValue = Math.max(4.0, Math.min(4.5, newValue));
    }

    const newBoundaries = {
      ...boundaries,
      [dragging]: Math.round(newValue * 10) / 10
    };

    console.log("BellCurveChart: Mouse move updating boundaries", newBoundaries);
    setBoundaries(newBoundaries);
    
    if (onBoundariesChange) {
      onBoundariesChange(newBoundaries);
    }

    event.preventDefault();
  }, [dragging, isDragging, boundaries, onBoundariesChange]);

  const handleMouseUp = (event: React.MouseEvent) => {
    setDragging(null);
    setIsDragging(false);
    event.preventDefault();
  };

  const handleMouseLeave = () => {
    setDragging(null);
    setIsDragging(false);
  };

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex justify-between items-center flex-shrink-0">
        <div className="text-white">
          <div className="flex gap-4 text-xs">
            <span className="text-white text-[12px]">Guideline:</span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-blue-400 rounded"></div>
              Band A
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-green-400 rounded"></div>
              Band B
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-yellow-400 rounded"></div>
              Band C
            </span>
            <span className="flex items-center gap-1">
              <div className="w-3 h-3 bg-red-400 rounded"></div>
              Band D
            </span>
          </div>
        </div>
      </div>
      
      <div className="flex-1 flex items-center justify-center">
        <Card className="bg-black p-4 w-full h-[340px]">
          <canvas
            ref={canvasRef}
            width={width}
            height={height}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseLeave}
            style={{ 
              cursor: isDragging ? 'grabbing' : (dragging ? 'grab' : 'default'), 
              width: '100%', 
              height: 'auto' 
            }}
            className="border border-primary/20"
          />
        </Card>
      </div>

      <div className="grid grid-cols-4 gap-0 text-[12px] text-white w-full px-[5px] flex-1 flex items-center justify-center mx-[45px]">
        <div>
          <label>Band D Boundary:</label>
          <div className="text-primary font-bold">{(boundaries.line1 || 0).toFixed(2)}</div>
        </div>
        <div>
          <label>Band C Boundary:</label>
          <div className="text-primary font-bold">{(boundaries.line2 || 0).toFixed(2)}</div>
        </div>
        <div>
          <label>Band B Boundary:</label>
          <div className="text-primary font-bold">{(boundaries.line3 || 0).toFixed(2)}</div>
        </div>
        <div>
          <label>Band A Boundary:</label>
          <div className="text-primary font-bold">{(boundaries.line4 || 5.0).toFixed(2)}</div>
        </div>
      </div>
    </div>
  );
};

export default BellCurveChart;
